import React, { useCallback, useEffect, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import FormDrawerWrapper from '../../features/components/FormDrawerWrapper'
import EditSectionsList from '../../features/components/Forms/EditForm/EditSectionsList'
import RepresentativePreview from './RepresentativeSection/RepresentativePreview'
import RepresentativeUpdateForm from './RepresentativeSection/RepresentativeUpdateForm'
import DeleteRepresentativeButton from './RepresentativeSection/DeleteRepresentativeButton'
import CreateRepresentativeForm from './CreateRepresentativeForm'

import useManageEditFormData from '../../hooks/formHooks/useManageEditFormData'
import { getRepresentativeTitle } from './RepresentativeSection/helpers'

import {
  getSelfAccountRepresentatives,
  clearGetSelfAccountRepresentatives,
  deleteSelfAccountRepresentative
} from '../../modules/actions/selfAccounts'
import {
  getSelfAccountRepresentativesSelector,
  getSelfAccountRepresentativesWasLoadedSelector,
  getSelfAccountRepresentativesIsLoadingSelector,
  updateSelfAccountRepresentativeIsLoadingSelector,
  updateSelfAccountRepresentativeWasUpdatedSelector
} from '../../modules/selectors/selfAccounts'
import { choicesCountriesWasLoadedSelector } from '../../modules/selectors/choices'
import { getChoicesCountries } from '../../modules/actions/choices'
import { formsOpenStatesSelector } from '../../modules/selectors/forms'

import { MANAGE_CLIENT_ACCOUNT_REPRESENTATIVES } from '../../constants/forms'

import useStyles from './styles'

function ManageClientAccountRepresentatives() {
  const classes = useStyles()
  const dispatch = useDispatch()

  const selfAccountRepresentatives = useSelector(getSelfAccountRepresentativesSelector)
  const isItemLoading = useSelector(getSelfAccountRepresentativesIsLoadingSelector)
  const itemWasLoaded = useSelector(getSelfAccountRepresentativesWasLoadedSelector)
  const updateSelfAccountRepresentativeIsLoading = useSelector(updateSelfAccountRepresentativeIsLoadingSelector)
  const updateSelfAccountRepresentativeWasUpdated = useSelector(updateSelfAccountRepresentativeWasUpdatedSelector)
  const { [MANAGE_CLIENT_ACCOUNT_REPRESENTATIVES]: isFormOpen = false } = useSelector(formsOpenStatesSelector)
  const choicesCountriesWasLoaded = useSelector(choicesCountriesWasLoadedSelector)

  // to avoid loading representation
  const isInitialDataLoading = isItemLoading || !itemWasLoaded

  const getItemDataHandler = useCallback(
    selectedEditItemId => {
      // won’t need pagination there shouldn’t be more than 10 per account
      dispatch(getSelfAccountRepresentatives({ account: selectedEditItemId }))
    },
    [dispatch]
  )

  const clearItemDataHandler = useCallback(() => {
    dispatch(clearGetSelfAccountRepresentatives())
  }, [dispatch])

  // all items should have the same account id - so use the first one
  const accountId = selfAccountRepresentatives?.[0]?.account

  useManageEditFormData({
    formName: MANAGE_CLIENT_ACCOUNT_REPRESENTATIVES,
    loadedDataId: accountId,
    getDataHandler: getItemDataHandler,
    clearDataHandler: clearItemDataHandler
  })
  // here defined initial open states for function ManageClientAccountRepresentatives sections
  const sectionsInitialOpenStates = useMemo(
    () =>
      selfAccountRepresentatives.reduce((acc, { id }) => {
        acc[id] = false
        return acc
      }, {}),
    [selfAccountRepresentatives]
  )

  const isOpenEditSectionButtonDisabled = useMemo(() => {
    return (
      // disable opening another section when one is updating to simplify the process
      updateSelfAccountRepresentativeIsLoading ||
      // also disable when it's in wasUpdated: true state (we need to wait when the progress button fires the clear action)
      updateSelfAccountRepresentativeWasUpdated
    )
  }, [updateSelfAccountRepresentativeIsLoading, updateSelfAccountRepresentativeWasUpdated])

  const deleteRepresentativeHandler = useCallback(
    id => {
      dispatch(deleteSelfAccountRepresentative(id))
    },
    [dispatch]
  )

  // here defined all edit sections for ManageClientAccountRepresentatives
  const editSections = useMemo(
    () =>
      selfAccountRepresentatives.map(representative => ({
        sectionName: representative.id,
        title: getRepresentativeTitle(representative),
        onDelete: () => deleteRepresentativeHandler(representative.id),
        isOpenEditSectionButtonDisabled,
        CustomDeleteButton: <DeleteRepresentativeButton id={representative.id} />,
        PreviewComponent: <RepresentativePreview />,
        FormComponent: <RepresentativeUpdateForm />
      })),
    [selfAccountRepresentatives, isOpenEditSectionButtonDisabled, deleteRepresentativeHandler]
  )

  useEffect(() => {
    if (isFormOpen && !choicesCountriesWasLoaded) {
      dispatch(getChoicesCountries())
    }
  }, [dispatch, isFormOpen, choicesCountriesWasLoaded])

  return (
    <FormDrawerWrapper
      title="Manage customer representatives"
      formName={MANAGE_CLIENT_ACCOUNT_REPRESENTATIVES}
      showOpenButton={false}
      // show loader until account is loaded
      isFormLoading={isInitialDataLoading}
    >
      <EditSectionsList
        editSections={editSections}
        isEditDataLoading={isInitialDataLoading}
        editItemData={selfAccountRepresentatives}
        sectionsInitialOpenStates={sectionsInitialOpenStates}
        className={classes.editSectionsList}
      />
      <CreateRepresentativeForm accountId={accountId} />
    </FormDrawerWrapper>
  )
}

export default ManageClientAccountRepresentatives
